import { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { emptyTaskExample, TaskExample } from "../../interfaces/Task";
import TaskExampleAdd from "./TaskExampleAdd";
import TaskExampleDelete from "./TaskExampleDelete";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "../../elements/LoadingSpinner";
import { MainLayout } from "../../layout/MainLayout";

export default function TaskExamples() {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const isAdmin = localStorage.getItem('isAdmin');

    const [taskExamples, setTaskExamples] = useState<TaskExample[]>([]);
    const [selectedTaskExample, setSelectedTaskExample] = useState<TaskExample>(emptyTaskExample);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [showDeleteForm, setShowDeleteForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [reloadData, setReloadData] = useState(0);

    const columns: TableColumn<TaskExample>[] = [
        {
            name: t("Id"),
            selector: (row: TaskExample) => row._id,
            sortable: true,
            width: '200px',
            omit: true, // Hide on mobile, will show in expandable row
        },
        {
            name: t("Title"),
            selector: (row: TaskExample) => row.task?.name || '',
            sortable: true,
            grow: 2,
        },
        {
            name: t("Actions"),
            cell: (row: TaskExample, index: number) => (
                <div className="actions">
                    <a
                        className="sort-up"
                        title={t("Move up")}
                        href="#"
                        onClick={(event) => handleSortUp(event, index)}
                    ></a>
                    <a
                        className="sort-down"
                        title={t("Move down")}
                        href="#"
                        onClick={(event) => handleSortDown(event, index)}
                    ></a>
                    <a
                        className="delete-link"
                        title={t("Delete task example")}
                        href="#"
                        onClick={(event) => handleShowDeleteForm(event, row._id)}
                    ></a>
                </div>
            ),
            width: '150px',
        },
    ];

    const ExpandedComponent = ({ data }: { data: TaskExample }) => (
        <div style={{ padding: '15px' }}>
            <div><strong>{t("Id")}:</strong> {data._id}</div>
        </div>
    );

    useEffect(() => {
        setLoading(true);
        const requestOptionsPost = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/examples', requestOptionsPost)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {
                setTaskExamples(data);
                setLoading(false);
            });
    }, [reloadData]);

    const handleShowAddForm = () => {
        setShowAddForm(true);
    }

    const handleHideAddForm = () => {
        setShowAddForm(false);
    }

    const handleSumbitAddForm = (te: TaskExample) => {
        setShowAddForm(false);
        setReloadData(prev => prev + 1);
    }
    
    const handleShowDeleteForm = (event: any, id: string) => {
        event.preventDefault()
        const newSelectedTaskExample: TaskExample = taskExamples.find((t: TaskExample) => t._id == id) ?? emptyTaskExample;
        if (newSelectedTaskExample._id != '') {
            setSelectedTaskExample(newSelectedTaskExample);
            setShowDeleteForm(true);
        } else {
            alert(t("No task example selected."));
        }
    }
    
    const handleHideDeleteForm = () => {
        setShowDeleteForm(false);
    }

    const handleSumbitDeleteForm = (id: string) => {
        setShowDeleteForm(false);
        setReloadData(prev => prev + 1);
    }

    const handleSortUp = (event: any, position: number) => {
        event.preventDefault();
        if (position > 0) {
            const newTaskExamples: TaskExample[] = taskExamples.map((pt: TaskExample, index) => {
                let t: TaskExample = Object.assign({}, pt); 
                t.weight = index;
                if (index == position - 1) {
                    t.weight = position;
                    
                }
                if (index == position) {
                    t.weight = position - 1;
                }
                return (t);
            });
            newTaskExamples.sort((a: TaskExample, b: TaskExample) => a.weight - b.weight);
            setTaskExamples(newTaskExamples);
            
            for (let i = 0; i < newTaskExamples.length; i++) {
                const requestOptions = {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + localStorage.getItem('token')
                    },
                    body: JSON.stringify(newTaskExamples[i])
                };
                fetch(import.meta.env.VITE_API_URL + '/v1/examples/update/' + newTaskExamples[i]._id, requestOptions)
                    .then(response => {
                    if (response.status !== 200) {
                        navigate('/');
                    }
                    return response.json();
                }).then(data => {});
            }
        }
    }

    const handleSortDown = (event: any, position: number) => {
        event.preventDefault();
        if (position < taskExamples.length - 1) {
            const newTaskExamples: TaskExample[] = taskExamples.map((pt: TaskExample, index) => {
                let t: TaskExample = Object.assign({}, pt); 
                t.weight = index;
                if (index == position + 1) {
                    t.weight = position;
                }
                if (index == position) {
                    t.weight = position + 1;
                }
                return (t);
            });
            newTaskExamples.sort((a: TaskExample, b: TaskExample) => a.weight - b.weight);
            setTaskExamples(newTaskExamples);

            for (let i = 0; i < newTaskExamples.length; i++) {
                const requestOptions = {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + localStorage.getItem('token')
                    },
                    body: JSON.stringify(newTaskExamples[i])
                };
                fetch(import.meta.env.VITE_API_URL + '/v1/examples/update/' + newTaskExamples[i]._id, requestOptions)
                    .then(response => {
                    if (response.status !== 200) {
                        navigate('/');
                    }
                    return response.json();
                }).then(data => {});
            }
        }
    }

    return (
        <MainLayout>
            <div className="container-fluid">
                <div className="row">
                    { isAdmin &&
                        <div className="admin-taskexamples-container">
                            <div className="page-header">
                                <h1>Task Examples management</h1>
                                <div className="page-actions">
                                    <a className="action-icon" onClick={ handleShowAddForm } title={ t("New Task Example") }>
                                        <i className="bi bi-plus-circle"></i>
                                    </a>
                                </div>
                            </div>
                            <div className="admin-taskexamples-table-container">
                                <DataTable
                                    columns={columns}
                                    data={taskExamples.filter((te: TaskExample) => te.task)}
                                    progressPending={loading}
                                    progressComponent={<LoadingSpinner />}
                                    pagination
                                    paginationPerPage={10}
                                    paginationRowsPerPageOptions={[10, 20, 30, 50]}
                                    highlightOnHover
                                    striped
                                    responsive
                                    noDataComponent={t("No task examples found")}
                                    expandableRows
                                    expandableRowsComponent={ExpandedComponent}
                                />
                            </div>
                        </div>
                    }
                    { !isAdmin &&
                        <div className="admin-taskexamples-container">
                            { t("You are not allowed to access this section.") }
                        </div>
                    }
                </div>
            </div>
            { showAddForm &&
                <TaskExampleAdd submitCallback={ handleSumbitAddForm } closeCallback={ handleHideAddForm } />
            }
            {showDeleteForm &&
                <TaskExampleDelete taskExample={ selectedTaskExample } submitCallback={ handleSumbitDeleteForm } closeCallback={ handleHideDeleteForm } />
            }
        </MainLayout>);
}